import React, { ReactNode } from 'react';
import {
    Box,
    Typography,
    Paper,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    SxProps,
    Theme,
    TablePagination
} from '@mui/material';

export interface TableColumn<T> {
    /** Column key that matches the data property */
    key: keyof T | string;
    /** Column header label */
    label: string;
    /** Column alignment */
    align?: 'left' | 'right' | 'center';
    /** Hide column on specific breakpoints */
    hideOn?: {
        xs?: boolean;
        sm?: boolean;
        md?: boolean;
        lg?: boolean;
    };
    /** Custom render function for cell content */
    render?: (item: T) => ReactNode;
    /** Custom header styles */
    headerSx?: SxProps<Theme>;
    /** Custom cell styles */
    cellSx?: SxProps<Theme>;
    /** Column width */
    width?: string | number;
}

export interface TableListProps<T> {
    /** Array of data items to display */
    data: T[];
    /** Column definitions */
    columns: TableColumn<T>[];
    /** Optional title for the table */
    title?: string;
    /** Optional custom component to render in header (e.g., export button) */
    headerActions?: ReactNode;
    /** Optional footer content (e.g., pagination, export button) */
    footer?: ReactNode;
    /** Function to get unique key for each row */
    getRowKey: (item: T) => string | number;
    /** Enable row hover effect */
    rowHover?: boolean;
    /** Custom styles for the Paper container */
    containerSx?: SxProps<Theme>;
    /** Custom styles for the Table */
    tableSx?: SxProps<Theme>;
    /** Minimum width for table (for horizontal scroll) */
    minWidth?: number | { xs?: number; sm?: number; md?: number };
    /** Custom empty state message */
    emptyMessage?: string;
    /** Show/hide table header background */
    headerBackground?: boolean;
    /** Enable pagination */
    enablePagination?: boolean;
    /** Current page (0-indexed) */
    page?: number;
    /** Rows per page */
    rowsPerPage?: number;
    /** Total count of items (for backend pagination) */
    totalCount?: number;
    /** Available rows per page options */
    rowsPerPageOptions?: number[];
    /** Callback when page changes */
    onPageChange?: (page: number) => void;
    /** Callback when rows per page changes */
    onRowsPerPageChange?: (rowsPerPage: number) => void;
    /** Has next page (for cursor-based pagination) */
    hasNextPage?: boolean;
    /** Has previous page (for cursor-based pagination) */
    hasPreviousPage?: boolean;
}

const TableList = React.memo(function TableList<T>({
    data,
    columns,
    title,
    headerActions,
    footer,
    getRowKey,
    rowHover = true,
    containerSx = {},
    tableSx = {},
    minWidth = { xs: 600, sm: 650 },
    emptyMessage = 'No hay datos disponibles',
    headerBackground = true,
    enablePagination = false,
    page = 0,
    rowsPerPage = 8,
    totalCount,
    rowsPerPageOptions = [8, 16, 24, 32],
    onPageChange,
    onRowsPerPageChange,
    hasNextPage,
    hasPreviousPage,
}: TableListProps<T>) {

    // Handle page change
    const handleChangePage = (_event: unknown, newPage: number) => {
        if (onPageChange) {
            onPageChange(newPage);
        }
    };

    // Handle rows per page change
    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        if (onRowsPerPageChange) {
            onRowsPerPageChange(newRowsPerPage);
        }
        if (onPageChange) {
            onPageChange(0); // Reset to first page
        }
    };

    const getDisplayValue = (hideOn?: TableColumn<T>['hideOn']) => {
        if (!hideOn) return undefined;

        return {
            xs: hideOn.xs ? 'none' : 'table-cell',
            sm: hideOn.sm ? 'none' : 'table-cell',
            md: hideOn.md ? 'none' : 'table-cell',
            lg: hideOn.lg ? 'none' : 'table-cell',
        };
    };

    const renderCellContent = (item: T, column: TableColumn<T>) => {
        if (column.render) {
            return column.render(item);
        }

        const value = item[column.key as keyof T];
        return value != null ? String(value) : '-';
    };

    return (
        <Paper
            elevation={0}
            sx={{
                border: '1px solid #E0E0E0',
                borderRadius: 2,
                overflow: 'hidden',
                px: { xs: 2, sm: 2.5, md: 3 },
                ...containerSx
            }}
        >
            {/* Header Section */}
            {(title || headerActions) && (
                <Box sx={{
                    p: { xs: 2, sm: 2.5, md: 3 },
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 2
                }}>
                    {title && (
                        <Typography
                            variant="h6"
                            fontWeight={600}
                            sx={{
                                textAlign: 'right',
                                width: '100%',
                                color: 'text.secondary',
                                fontSize: { xs: '0.5rem', sm: '0.7rem', md: '0.9rem' }
                            }}
                        >
                            {title}
                        </Typography>
                    )}
                    {headerActions && (
                        <Box sx={{ flexShrink: 0 }}>
                            {headerActions}
                        </Box>
                    )}
                </Box>
            )}

            {/* Table Section */}
            <TableContainer sx={{ overflowX: 'auto' }}>
                <Table
                    sx={{
                        minWidth: minWidth,
                        ...tableSx
                    }}
                >
                    {/* Table Head */}
                    <TableHead sx={headerBackground ? { backgroundColor: '#F8F9FA' } : {}}>
                        <TableRow>
                            {columns.map((column, index) => (
                                <TableCell
                                    key={`header-${String(column.key)}-${index}`}
                                    align={column.align || 'left'}
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                        py: { xs: 1.5, sm: 2 },
                                        width: column.width,
                                        display: getDisplayValue(column.hideOn),
                                        ...column.headerSx
                                    }}
                                >
                                    {column.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    {/* Table Body */}
                    <TableBody>
                        {data.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={columns.length}
                                    align="center"
                                    sx={{
                                        py: 4,
                                        color: 'text.secondary',
                                        fontSize: { xs: '0.875rem', sm: '1rem' }
                                    }}
                                >
                                    {emptyMessage}
                                </TableCell>
                            </TableRow>
                        ) : (
                            data.map((item) => (
                                <TableRow
                                    key={getRowKey(item)}
                                    hover={rowHover}
                                >
                                    {columns.map((column, index) => (
                                        <TableCell
                                            key={`cell-${String(column.key)}-${index}`}
                                            align={column.align || 'left'}
                                            sx={{
                                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                py: { xs: 1.5, sm: 2 },
                                                display: getDisplayValue(column.hideOn),
                                                ...column.cellSx
                                            }}
                                        >
                                            {renderCellContent(item, column)}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Footer Section */}
            {(footer || enablePagination) && (
                <Box sx={{
                    p: { xs: 2, sm: 2.5, md: 3 },
                    display: 'flex',
                    justifyContent: { xs: 'center', sm: 'space-between', md : 'flex-end' },
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2
                }}>
                    {footer && <Box>{footer}</Box>}
                    {enablePagination && (
                        <TablePagination
                            component="div"
                            count={totalCount ?? data.length}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={rowsPerPageOptions}
                            labelRowsPerPage="Filas por página:"
                            labelDisplayedRows={({ from, to, count }) =>
                                `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
                            }
                            slotProps={{
                                actions: {
                                    nextButton: {
                                        disabled: hasNextPage !== undefined ? !hasNextPage : undefined,
                                    },
                                    previousButton: {
                                        disabled: hasPreviousPage !== undefined ? !hasPreviousPage : undefined,
                                    },
                                },
                            }}
                            sx={{
                                border: 'none',
                                '.MuiTablePagination-toolbar': {
                                    minHeight: { xs: 48, sm: 52 },
                                },
                                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                },
                            }}
                        />
                    )}
                </Box>
            )}
        </Paper>
    );
}) as <T>(props: TableListProps<T>) => JSX.Element;

export default TableList;
